import { useState, useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import axios from 'axios';
import { motion } from 'framer-motion';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { AgentCard } from '@/components/AgentCard';
import { TommyLoading, TommyEmptyState } from '@/components/Tommy';
import { TommyBackground } from '@/components/TommyBackground';
import { Search, Filter, Bot, Loader2, BadgeCheck } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const TOMMY_IMAGE = "https://images.unsplash.com/photo-1762029027344-e100f27b65f9?crop=entropy&cs=srgb&fm=jpg&w=100";

const CATEGORIES = [
  'Customer Service',
  'Data Analysis',
  'Content Creation',
  'Software Development',
  'Marketing',
  'Research',
  'Translation',
  'Legal',
  'Finance',
  'Healthcare'
];

export default function BrowseAgents() {
  const [searchParams, setSearchParams] = useSearchParams();
  const [agents, setAgents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState(searchParams.get('search') || '');
  const [category, setCategory] = useState(searchParams.get('category') || '');
  const [verifiedOnly, setVerifiedOnly] = useState(searchParams.get('verified') === 'true');

  const fetchAgents = async () => {
    try {
      setLoading(true);
      const params = new URLSearchParams();
      if (search) params.append('search', search);
      if (category) params.append('category', category);
      if (verifiedOnly) params.append('verified_only', 'true');
      
      const response = await axios.get(`${API}/agents?${params.toString()}`);
      setAgents(response.data);
    } catch (error) {
      console.error('Error fetching agents:', error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchAgents();
  }, [category, verifiedOnly]);

  const handleSearch = (e) => {
    e.preventDefault();
    fetchAgents();
    const params = new URLSearchParams();
    if (search) params.set('search', search);
    if (category) params.set('category', category);
    if (verifiedOnly) params.set('verified', 'true');
    setSearchParams(params);
  };

  const handleCategoryChange = (value) => {
    setCategory(value === 'all' ? '' : value);
    const params = new URLSearchParams();
    if (search) params.set('search', search);
    if (value && value !== 'all') params.set('category', value);
    if (verifiedOnly) params.set('verified', 'true');
    setSearchParams(params);
  };

  const handleVerifiedToggle = () => {
    const newValue = !verifiedOnly;
    setVerifiedOnly(newValue);
    const params = new URLSearchParams();
    if (search) params.set('search', search);
    if (category) params.set('category', category);
    if (newValue) params.set('verified', 'true');
    setSearchParams(params);
  };

  return (
    <div className="pt-24 pb-16 min-h-screen relative" data-testid="browse-agents-page">
      {/* Tommy's animated background - Time-of-day aware! */}
      <TommyBackground useTimeOfDay={true} opacity={0.12} />
      
      <div className="max-w-7xl mx-auto px-6 relative z-10">
        {/* Header */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="mb-12"
        >
          <div className="flex items-center gap-3 mb-4">
            <div className="w-10 h-10 rounded-lg bg-cyan-500/20 flex items-center justify-center">
              <Bot className="w-5 h-5 text-cyan-400" />
            </div>
            <h1 className="text-3xl md:text-4xl font-bold text-white">Browse AI Agents</h1>
          </div>
          <p className="text-zinc-400">
            Discover powerful AI agents ready to work on your projects
          </p>
        </motion.div>

        {/* Filters */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.1 }}
          className="mb-8"
        >
          <form onSubmit={handleSearch} className="flex flex-col md:flex-row gap-4" role="search" aria-label="Search agents">
            <div className="flex-1 relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-zinc-500" aria-hidden="true" />
              <Input
                placeholder="Search agents by name, description, or capabilities..."
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                className="pl-10 bg-zinc-900/50 border-white/10 focus:border-cyan-500/50 h-12"
                data-testid="search-input"
                aria-label="Search agents"
              />
            </div>
            <Select value={category || 'all'} onValueChange={handleCategoryChange}>
              <SelectTrigger className="w-full md:w-[200px] bg-zinc-900/50 border-white/10 h-12" data-testid="category-select" aria-label="Filter by category">
                <Filter className="w-4 h-4 mr-2 text-zinc-500" aria-hidden="true" />
                <SelectValue placeholder="All Categories" />
              </SelectTrigger>
              <SelectContent className="bg-zinc-900 border-white/10">
                <SelectItem value="all">All Categories</SelectItem>
                {CATEGORIES.map((cat) => (
                  <SelectItem key={cat} value={cat}>{cat}</SelectItem>
                ))}
              </SelectContent>
            </Select>
            <Button 
              type="button"
              onClick={handleVerifiedToggle}
              className={`h-12 px-4 rounded-none flex items-center gap-2 ${
                verifiedOnly 
                  ? 'bg-cyan-500 hover:bg-cyan-600 text-white' 
                  : 'bg-zinc-900/50 border border-white/10 hover:border-cyan-500/50 text-zinc-300'
              }`}
              data-testid="verified-filter-btn"
              aria-label={verifiedOnly ? "Show all agents" : "Show only Tommy Approved agents"}
              aria-pressed={verifiedOnly}
            >
              <img src={TOMMY_IMAGE} alt="" className="w-5 h-5 rounded-full" aria-hidden="true" />
              <span className="hidden sm:inline">Tommy Approved</span>
              {verifiedOnly && <BadgeCheck className="w-4 h-4" aria-hidden="true" />}
            </Button>
            <Button 
              type="submit"
              className="bg-cyan-500 hover:bg-cyan-600 text-white h-12 px-8 rounded-none"
              data-testid="search-btn"
              aria-label="Search agents"
            >
              Search
            </Button>
          </form>
        </motion.div>

        {/* Results */}
        {loading ? (
          <TommyLoading text="Tommy is fetching agents..." />
        ) : agents.length === 0 ? (
          <TommyEmptyState
            title="No agents found"
            description="Tommy couldn't find any agents matching your search. Try adjusting your filters!"
          />
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
            {agents.map((agent, idx) => (
              <motion.div
                key={agent.id}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: idx * 0.05 }}
              >
                <AgentCard agent={agent} />
              </motion.div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
